import React from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import round from '../../../helpers/round';
import PriebeAreaRatioSketch from './PriebeAreaRatioSketch';
import PriebeChartSvg from './PriebeChartSvg';

const footingTypeLabels = {
  unlimitedArea: 'Unlimited area',
  singleFooting: 'Single footing',
  stripFooting: 'Strip footing',
};

const gridTypeLabels = {
  square: 'Square grid',
  triangular: 'Triangular grid',
};

const hasNumber = (value) => Number.isFinite(Number(value));

const formatNumber = (value, digits = 3) => (hasNumber(value) ? round(Number(value), digits) : 'N/A');
const formatFixed = (value, digits = 4) => (hasNumber(value) ? Number(value).toFixed(digits) : 'N/A');

const formatWithUnit = (value, digits, unit) => (hasNumber(value) ? `${round(Number(value), digits)} ${unit}` : 'N/A');

const ValueCard = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
  </Paper>
);

const CompactValueCard = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.1, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ mt: 0.35, lineHeight: 1.2, fontWeight: 700 }}>
      {value}
    </Typography>
  </Paper>
);

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' }, gap: 1, py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const MathLine = ({ children }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      sx={{
        minWidth: 'max-content',
        '& math': {
          fontSize: '1rem',
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

const StageSection = ({ stage, title, description, children }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={1.75}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
          pb: 0.25,
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.45,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          Stage {stage}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
      {children}
    </Stack>
  </Paper>
);

const ChartPanel = ({ chart }) => {
  if (!chart) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {chart.title}
      </Typography>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 620 }}>
          <PriebeChartSvg chart={chart} />
        </Box>
      </Box>
      {chart.note && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {chart.note}
        </Typography>
      )}
      {!!chart.selectedPoints?.length && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {chart.selectedPoints.map((point) => (
            <Chip key={`${chart.id}-${point.label}`} size="small" color="warning" variant="outlined" label={point.label} />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const ChartGrid = ({ charts }) => {
  const visibleCharts = charts.filter(Boolean);
  if (!visibleCharts.length) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          xl: visibleCharts.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        },
        gap: 2,
      }}
    >
      {visibleCharts.map((chart) => (
        <ChartPanel key={chart.id} chart={chart} />
      ))}
    </Box>
  );
};

const SettlementLayerRow = ({ layer }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '0.75fr 0.75fr 0.9fr 0.8fr 0.85fr 0.8fr 0.95fr 1fr',
      gap: 1,
      py: 1,
      borderTop: '1px solid',
      borderColor: 'divider',
      fontSize: 13,
      alignItems: 'center',
    }}
  >
    <Box>{formatWithUnit(layer.z1, 2, 'm')}</Box>
    <Box>{formatWithUnit(layer.z2, 2, 'm')}</Box>
    <Box>{layer.treated ? 'Treated' : 'Untreated'}</Box>
    <Box>{formatNumber(layer.Ds, 2)}</Box>
    <Box>{layer.treated ? formatNumber(layer.dcDs, 2) : '-'}</Box>
    <Box>{formatNumber(layer.n1, 3)}</Box>
    <Box>{formatNumber(layer.n2Avg, 3)}</Box>
    <Box>{formatWithUnit((layer.footingSettlementIncrement || 0) * 100, 2, 'cm')}</Box>
  </Box>
);

const SettlementLayerTable = ({ layers }) => {
  if (!layers.length) return null;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 760 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '0.75fr 0.75fr 0.9fr 0.8fr 0.85fr 0.8fr 0.95fr 1fr',
            gap: 1,
            pb: 1,
            fontWeight: 700,
          }}
        >
          <Box>z1</Box>
          <Box>z2</Box>
          <Box>Layer</Box>
          <Box>Ds</Box>
          <Box>Dc/Ds</Box>
          <Box>n1</Box>
          <Box>n2(avg)</Box>
          <Box>Settlement</Box>
        </Box>
        {layers.map((layer) => (
          <SettlementLayerRow key={layer.layerId} layer={layer} />
        ))}
      </Box>
    </Box>
  );
};

const BearingLayerRow = ({ layer }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '0.8fr 0.8fr 0.9fr 0.8fr 0.9fr 0.8fr 0.95fr',
      gap: 1,
      py: 1,
      borderTop: '1px solid',
      borderColor: 'divider',
      fontSize: 13,
      alignItems: 'center',
    }}
  >
    <Box>{formatWithUnit(layer.z1, 2, 'm')}</Box>
    <Box>{formatWithUnit(layer.z2, 2, 'm')}</Box>
    <Box>{layer.treated ? 'Treated' : 'Untreated'}</Box>
    <Box>{formatNumber(layer.eta0, 3)}</Box>
    <Box>{formatWithUnit(layer.dGr0, 2, 'm')}</Box>
    <Box>{formatNumber(layer.etaAveraged, 3)}</Box>
    <Box>{formatWithUnit(layer.dGrAveraged, 2, 'm')}</Box>
  </Box>
);

const BearingLayerTable = ({ layers }) => {
  if (!Array.isArray(layers) || !layers.length) return null;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 760 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '0.8fr 0.8fr 0.9fr 0.8fr 0.9fr 0.8fr 0.95fr',
            gap: 1,
            pb: 1,
            fontWeight: 700,
          }}
        >
          <Box>z1</Box>
          <Box>z2</Box>
          <Box>Layer</Box>
          <Box>eta0</Box>
          <Box>dGr,0</Box>
          <Box>eta(avg)</Box>
          <Box>dGr(avg)</Box>
        </Box>
        {layers.map((layer) => (
          <BearingLayerRow key={`bearing-${layer.layerId}`} layer={layer} />
        ))}
      </Box>
    </Box>
  );
};

const getGoverningCompatibilityControl = (layer) => {
  if (!layer || !hasNumber(layer.fdLimit1) || !hasNumber(layer.fdLimit2)) return 'N/A';

  if (Number(layer.fdLimit1) <= Number(layer.fdLimit2)) {
    return 'Control 1 governs: fd <= y * Dc/Ds';
  }

  return 'Control 2 governs: n <= 1 + (Ac/A) * Dc/Ds';
};

const PriebeResult = ({ result }) => {
  const inputs = result?.inputs || {};
  const outputs = result?.outputs || {};
  const geometry = outputs?.geometry || {};
  const factors = outputs?.factors || {};
  const settlements = outputs?.settlements || {};
  const bearingCapacity = outputs?.bearingCapacity || null;
  const charts = outputs?.charts || {};
  const layers = Array.isArray(factors?.layers) ? factors.layers : [];
  const topTreatedLayer = layers.find((layer) => layer.treated) || null;
  const canUserSeeCalculationSteps = Boolean(outputs?.canUserSeeCalculationSteps);
  const footingType = settlements?.footingType || inputs?.footingType || 'unlimitedArea';
  const footingTypeLabel = footingTypeLabels[footingType] || footingType;
  const gridTypeLabel = gridTypeLabels[inputs?.areaRatioGridType] || 'Grid not set';
  const muS = 1 / 3;
  const replacementRatioValue = hasNumber(geometry?.replacementRatio) ? Number(geometry.replacementRatio) : null;
  const n0AuxiliaryFactor =
    replacementRatioValue !== null
      ? ((1 - muS) * (1 - replacementRatioValue)) / (1 - 2 * muS + replacementRatioValue)
      : null;
  const representativeLayerLabel = topTreatedLayer?.layerId || 'Top treated layer';
  const figure1MaxY = Array.isArray(charts?.figure1?.yDomain) ? charts.figure1.yDomain[1] : null;
  const isFigure1PointAboveWindow = hasNumber(factors?.n0) && hasNumber(figure1MaxY) && Number(factors.n0) > Number(figure1MaxY);
  const canShowN0Trace = [inputs?.columnFrictionAngle, factors?.kAc, replacementRatioValue, n0AuxiliaryFactor, factors?.n0].every(hasNumber);
  const nMaxRepresentative =
    topTreatedLayer && hasNumber(geometry?.replacementRatio) && hasNumber(topTreatedLayer?.dcDs)
      ? 1 + Number(geometry.replacementRatio) * Number(topTreatedLayer.dcDs)
      : null;
  const spacingGreaterThanDiameter =
    hasNumber(inputs?.columnSpacing) && hasNumber(inputs?.columnDiameter) ? Number(inputs.columnSpacing) > Number(inputs.columnDiameter) : false;
  const areaRatioIsWithinSketchRange = hasNumber(geometry?.areaRatio) ? Number(geometry.areaRatio) >= 1 && Number(geometry.areaRatio) <= 10 : false;
  const geometryStageAreaRatioHelper = {
    canCompute: hasNumber(inputs?.columnSpacing) && hasNumber(inputs?.columnDiameter),
    spacingGreaterThanDiameter,
    isWithinChartRange: areaRatioIsWithinSketchRange,
    columnArea: geometry?.columnArea,
    tributaryArea: geometry?.tributaryArea,
    replacementRatio: geometry?.replacementRatio,
    calculatedAreaRatio: geometry?.areaRatio,
  };

  return (
    <Stack spacing={2.5} sx={{ mt: 2 }}>
      {Array.isArray(outputs?.warnings) &&
        outputs.warnings.map((warning) => (
          <Alert key={warning} severity="warning" variant="outlined">
            {warning}
          </Alert>
        ))}

      {!canUserSeeCalculationSteps && (
        <Alert severity="info" variant="outlined">
          Layer-by-layer details are limited on the basic plan. The staged Priebe workflow and governing chart selections are still shown below.
        </Alert>
      )}

      <StageSection
        stage="1"
        title="Geometry"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(320px, 0.95fr) minmax(360px, 1.15fr)',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            <CompactValueCard label="Grid type" value={gridTypeLabel} />
            <CompactValueCard label="Column diameter D" value={formatWithUnit(inputs.columnDiameter, 3, 'm')} />
            <CompactValueCard label="Spacing s" value={formatWithUnit(inputs.columnSpacing, 3, 'm')} />
            <CompactValueCard label="Treatment depth d" value={formatWithUnit(inputs.treatmentDepth, 3, 'm')} />
            <CompactValueCard label="Column area Ac" value={formatWithUnit(geometry.columnArea, 4, 'm²')} />
            <CompactValueCard label="Tributary area A" value={formatWithUnit(geometry.tributaryArea, 4, 'm²')} />
            <CompactValueCard label="Area ratio A/Ac" value={formatNumber(geometry.areaRatio, 3)} />
            <CompactValueCard label="Replacement ratio Ac/A" value={formatNumber(geometry.replacementRatio, 3)} />
          </Box>

          <Paper variant="outlined" sx={{ p: 1, maxWidth: 360, width: '100%', justifySelf: 'start', alignSelf: 'start' }}>
            <PriebeAreaRatioSketch
              gridType={inputs?.areaRatioGridType}
              columnSpacing={inputs?.columnSpacing}
              columnDiameter={inputs?.columnDiameter}
              areaRatioHelper={geometryStageAreaRatioHelper}
              effectiveAreaRatio={geometry?.areaRatio}
              showSummaryPanel={false}
              showHelperText={false}
            />
          </Paper>
        </Box>
      </StageSection>

      <StageSection
        stage="2"
        title="Basic Improvement"
        description="Using the area ratio A/Ac and the stone-column friction angle phi_c, determine the basic improvement factor n0 from Section 2 and Figure 1."
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(320px, 0.95fr) minmax(360px, 1.15fr)',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack spacing={1.25}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              <CompactValueCard label="Column friction angle phi_c" value={formatWithUnit(inputs.columnFrictionAngle, 1, 'deg')} />
              <CompactValueCard label="Ka,c" value={formatNumber(factors.kAc, 4)} />
              <CompactValueCard label="Area ratio A/Ac" value={formatNumber(geometry.areaRatio, 3)} />
              <CompactValueCard label="Basic improvement factor n0" value={formatNumber(factors.n0, 3)} />
            </Box>

            {canShowN0Trace && (
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">n0 calculation trace</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Using Priebe&apos;s Section 2 equation with μs = 1/3.
                  </Typography>

                  <MathLine>
                    <math display="block">
                      <mrow>
                        <msub>
                          <mi>K</mi>
                          <mrow>
                            <mi>a</mi>
                            <mo>,</mo>
                            <mi>c</mi>
                          </mrow>
                        </msub>
                        <mo>=</mo>
                        <msup>
                          <mrow>
                            <mi>tan</mi>
                            <mo>(</mo>
                            <mn>45</mn>
                            <mo>°</mo>
                            <mo>-</mo>
                            <mfrac>
                              <msub>
                                <mi>φ</mi>
                                <mi>c</mi>
                              </msub>
                              <mn>2</mn>
                            </mfrac>
                            <mo>)</mo>
                          </mrow>
                          <mn>2</mn>
                        </msup>
                        <mo>,</mo>
                        <mspace width="0.8em" />
                        <mi>f</mi>
                        <mo>=</mo>
                        <mfrac>
                          <mrow>
                            <mo>(</mo>
                            <mn>1</mn>
                            <mo>-</mo>
                            <msub>
                              <mi>μ</mi>
                              <mi>s</mi>
                            </msub>
                            <mo>)</mo>
                            <mo>(</mo>
                            <mn>1</mn>
                            <mo>-</mo>
                            <mfrac>
                              <msub>
                                <mi>A</mi>
                                <mi>c</mi>
                              </msub>
                              <mi>A</mi>
                            </mfrac>
                            <mo>)</mo>
                          </mrow>
                          <mrow>
                            <mn>1</mn>
                            <mo>-</mo>
                            <mn>2</mn>
                            <msub>
                              <mi>μ</mi>
                              <mi>s</mi>
                            </msub>
                            <mo>+</mo>
                            <mfrac>
                              <msub>
                                <mi>A</mi>
                                <mi>c</mi>
                              </msub>
                              <mi>A</mi>
                            </mfrac>
                          </mrow>
                        </mfrac>
                        <mo>,</mo>
                        <mspace width="0.8em" />
                        <msub>
                          <mi>n</mi>
                          <mn>0</mn>
                        </msub>
                        <mo>=</mo>
                        <mn>1</mn>
                        <mo>+</mo>
                        <mfrac>
                          <msub>
                            <mi>A</mi>
                            <mi>c</mi>
                          </msub>
                          <mi>A</mi>
                        </mfrac>
                        <mo>[</mo>
                        <mfrac>
                          <mrow>
                            <mn>0.5</mn>
                            <mo>+</mo>
                            <mi>f</mi>
                          </mrow>
                          <mrow>
                            <msub>
                              <mi>K</mi>
                              <mrow>
                                <mi>a</mi>
                                <mo>,</mo>
                                <mi>c</mi>
                              </mrow>
                            </msub>
                            <mi>f</mi>
                          </mrow>
                        </mfrac>
                        <mo>-</mo>
                        <mn>1</mn>
                        <mo>]</mo>
                      </mrow>
                    </math>
                  </MathLine>

                  <MathLine>
                    <math display="block">
                      <mrow>
                        <msub>
                          <mi>K</mi>
                          <mrow>
                            <mi>a</mi>
                            <mo>,</mo>
                            <mi>c</mi>
                          </mrow>
                        </msub>
                        <mo>=</mo>
                        <msup>
                          <mrow>
                            <mi>tan</mi>
                            <mo>(</mo>
                            <mn>45</mn>
                            <mo>°</mo>
                            <mo>-</mo>
                            <mfrac>
                              <mn>{formatFixed(inputs.columnFrictionAngle, 1)}</mn>
                              <mn>2</mn>
                            </mfrac>
                            <mo>)</mo>
                          </mrow>
                          <mn>2</mn>
                        </msup>
                        <mo>=</mo>
                        <mn>{formatFixed(factors.kAc, 4)}</mn>
                        <mo>,</mo>
                        <mspace width="0.8em" />
                        <mi>f</mi>
                        <mo>=</mo>
                        <mfrac>
                          <mrow>
                            <mo>(</mo>
                            <mn>1</mn>
                            <mo>-</mo>
                            <mn>{formatFixed(muS, 4)}</mn>
                            <mo>)</mo>
                            <mo>(</mo>
                            <mn>1</mn>
                            <mo>-</mo>
                            <mn>{formatFixed(replacementRatioValue, 4)}</mn>
                            <mo>)</mo>
                          </mrow>
                          <mrow>
                            <mn>1</mn>
                            <mo>-</mo>
                            <mn>2</mn>
                            <mo>×</mo>
                            <mn>{formatFixed(muS, 4)}</mn>
                            <mo>+</mo>
                            <mn>{formatFixed(replacementRatioValue, 4)}</mn>
                          </mrow>
                        </mfrac>
                        <mo>=</mo>
                        <mn>{formatFixed(n0AuxiliaryFactor, 4)}</mn>
                        <mo>,</mo>
                        <mspace width="0.8em" />
                        <msub>
                          <mi>n</mi>
                          <mn>0</mn>
                        </msub>
                        <mo>=</mo>
                        <mn>1</mn>
                        <mo>+</mo>
                        <mn>{formatFixed(replacementRatioValue, 4)}</mn>
                        <mo>[</mo>
                        <mfrac>
                          <mrow>
                            <mn>0.5</mn>
                            <mo>+</mo>
                            <mn>{formatFixed(n0AuxiliaryFactor, 4)}</mn>
                          </mrow>
                          <mrow>
                            <mn>{formatFixed(factors.kAc, 4)}</mn>
                            <mo>×</mo>
                            <mn>{formatFixed(n0AuxiliaryFactor, 4)}</mn>
                          </mrow>
                        </mfrac>
                        <mo>-</mo>
                        <mn>1</mn>
                        <mo>]</mo>
                      </mrow>
                    </math>
                  </MathLine>

                  <MathLine>
                    <math display="block">
                      <mrow>
                        <msub>
                          <mi>n</mi>
                          <mn>0</mn>
                        </msub>
                        <mo>=</mo>
                        <mn>{formatFixed(factors.n0, 3)}</mn>
                      </mrow>
                    </math>
                  </MathLine>
                </Stack>
              </Paper>
            )}

            {isFigure1PointAboveWindow && (
              <Alert severity="info" variant="outlined">
                The computed n0 is above the Figure 1 display window. The result is still calculated from Priebe&apos;s equation; the chart is used here for verification only.
              </Alert>
            )}
          </Stack>

          <Paper variant="outlined" sx={{ p: 1.25, maxWidth: 540, width: '100%', justifySelf: 'start' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Figure 1 verification
            </Typography>
            <PriebeChartSvg chart={charts.figure1} width={560} height={330} />
            {charts.figure1?.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {charts.figure1.note}
              </Typography>
            )}
            {!!charts.figure1?.selectedPoints?.length && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {charts.figure1.selectedPoints.map((point) => (
                  <Chip key={`stage-2-${point.label}`} size="small" color="warning" variant="outlined" label={point.label} />
                ))}
              </Stack>
            )}
          </Paper>
        </Box>
      </StageSection>

      {topTreatedLayer && (
        <StageSection
          stage="3"
          title="Compressibility Correction"
          description="Section 3 reduces the basic factor for finite column compressibility. The representative values below use the top treated layer."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ValueCard label="Representative layer" value={representativeLayerLabel} />
            <ValueCard label="Dc/Ds" value={formatNumber(topTreatedLayer.dcDs, 3)} />
            <ValueCard label="A/Ac at n = Dc/Ds" value={formatNumber(topTreatedLayer.equivalentAreaRatio, 3)} />
            <ValueCard label="Delta(A/Ac)" value={formatNumber(topTreatedLayer.deltaAreaRatio, 3)} />
            <ValueCard label="Adjusted A/Ac" value={formatNumber(topTreatedLayer.adjustedAreaRatio, 3)} />
            <ValueCard label="Corrected improvement factor n1" value={formatNumber(topTreatedLayer.n1, 3)} />
          </Box>

          <ChartGrid charts={[charts.figure2]} />
        </StageSection>
      )}

      {topTreatedLayer && (
        <StageSection
          stage="4"
          title="Overburden and Depth"
          description="Section 4 accounts for overburden support through the depth factor fd, increasing n1 to the final improvement factor n2."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ValueCard label="At-rest coefficient K0,c" value={formatNumber(factors.k0c, 4)} />
            <ValueCard label="Depth influence factor y" value={formatNumber(factors.depthInfluenceFactor, 4)} />
            <ValueCard label="Average n2" value={formatNumber(topTreatedLayer.n2Avg, 3)} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <Box>
              <DetailRow label="Overburden at z1" value={formatWithUnit(topTreatedLayer.overburdenTop, 2, 'kPa')} />
              <DetailRow label="Overburden at mid-depth" value={formatWithUnit(topTreatedLayer.overburdenMid, 2, 'kPa')} />
              <DetailRow label="Overburden at z2" value={formatWithUnit(topTreatedLayer.overburdenBottom, 2, 'kPa')} />
            </Box>
            <Box>
              <DetailRow label="Raw fd at z1" value={formatNumber(topTreatedLayer.fdRawTop, 3)} />
              <DetailRow label="Raw fd at z2" value={formatNumber(topTreatedLayer.fdRawBottom, 3)} />
              <DetailRow label="Applied fd at z1" value={formatNumber(topTreatedLayer.fdTop, 3)} />
              <DetailRow label="Applied fd at z2" value={formatNumber(topTreatedLayer.fdBottom, 3)} />
            </Box>
          </Box>

          <ChartGrid charts={[charts.figure3]} />
        </StageSection>
      )}

      {topTreatedLayer && (
        <StageSection
          stage="5"
          title="Compatibility Controls"
          description="Priebe applies compatibility checks so the load assigned to the columns remains consistent with their compressibility."
        >
          <Alert severity="info" variant="outlined">
            {getGoverningCompatibilityControl(topTreatedLayer)}
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ValueCard label="Compatibility factor y" value={formatNumber(factors.depthCompatibilityInfluence, 4)} />
            <ValueCard label="fd limit 1" value={formatNumber(topTreatedLayer.fdLimit1, 3)} />
            <ValueCard label="fd limit 2" value={formatNumber(topTreatedLayer.fdLimit2, 3)} />
            <ValueCard label="Maximum factor nmax" value={formatNumber(nMaxRepresentative, 3)} />
            <ValueCard label="Applied fd at z1" value={formatNumber(topTreatedLayer.fdTop, 3)} />
            <ValueCard label="Applied fd at z2" value={formatNumber(topTreatedLayer.fdBottom, 3)} />
          </Box>

          <ChartGrid charts={[charts.figure4]} />
        </StageSection>
      )}

      {topTreatedLayer && (
        <StageSection
          stage="6"
          title="Derived Design Values"
          description="Section 6 converts the improvement factors into load sharing, residual soil stress, and equivalent composite shear parameters."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                From n0
              </Typography>
              <DetailRow label="Exact column load share m" value={formatNumber(factors.proportionalLoadExact, 3)} />
              <DetailRow label="Reduced column load share m'" value={formatNumber(factors.proportionalLoadReduced, 3)} />
              <DetailRow label="Residual soil stress ratio ps/p" value={formatNumber(factors.residualSoilStressRatio, 4)} />
              <DetailRow label="Column stress ratio pc/p" value={formatNumber(factors.columnStressRatio, 4)} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Representative treated layer
              </Typography>
              <DetailRow label="Reduced load share from n1" value={formatNumber(topTreatedLayer.m1Reduced, 3)} />
              <DetailRow label="Reduced load share from n2" value={formatNumber(topTreatedLayer.m2Reduced, 3)} />
              <DetailRow label="Composite friction angle at n1" value={formatWithUnit(topTreatedLayer.phiCompositeN1, 2, 'deg')} />
              <DetailRow label="Composite friction angle at n2" value={formatWithUnit(topTreatedLayer.phiCompositeN2, 2, 'deg')} />
              <DetailRow label="Reduced cohesion at n1" value={formatWithUnit(topTreatedLayer.cLoadN1, 3, 'kPa')} />
              <DetailRow label="Reduced cohesion at n2" value={formatWithUnit(topTreatedLayer.cLoadN2, 3, 'kPa')} />
              <DetailRow label="Liquefaction reduction factor" value={formatNumber(topTreatedLayer.liquefactionReductionFactor, 3)} />
            </Paper>
          </Box>

          <ChartGrid charts={[charts.figure5, charts.figure8]} />
        </StageSection>
      )}

      <StageSection
        stage="7"
        title="Settlement"
        description="Section 7 first evaluates the equivalent unlimited-area settlement, then applies the footing or strip-footing reduction where relevant."
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          <ValueCard label="Footing case" value={footingTypeLabel} />
          <ValueCard label="Infinite-area settlement s∞" value={formatWithUnit(settlements.totalInfiniteAreaSettlementCm, 2, 'cm')} />
          <ValueCard label="Final settlement s" value={formatWithUnit(settlements.totalFootingSettlementCm, 2, 'cm')} />
          <ValueCard label="Settlement ratio s/s∞" value={footingType === 'unlimitedArea' ? '1.00' : formatNumber(settlements.footingToInfiniteRatio, 3)} />
        </Box>

        {canUserSeeCalculationSteps && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Layer-by-layer settlement contribution</Typography>
            <SettlementLayerTable layers={layers} />
          </Stack>
        )}

        <ChartGrid charts={[charts.figure6, charts.figure7]} />
      </StageSection>

      {bearingCapacity && (
        <StageSection
          stage="8"
          title="Bearing Capacity"
          description="Section 8 uses Priebe's fictitious footing width to estimate bearing capacity on the improved ground."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ValueCard label="Fictitious footing width" value={formatWithUnit(bearingCapacity.fictitiousWidth, 3, 'm')} />
            <ValueCard label="Safety factor eta" value={formatNumber(bearingCapacity.safetyFactor, 3)} />
            <ValueCard label="Ultimate bearing pressure" value={formatWithUnit(bearingCapacity.ultimateBearingPressure, 2, 'kPa')} />
            <ValueCard label="Improved friction angle" value={formatWithUnit(bearingCapacity.improvedFrictionAngle, 2, 'deg')} />
            <ValueCard label="Native friction angle" value={formatWithUnit(bearingCapacity.nativeFrictionAngle, 2, 'deg')} />
            <ValueCard label="Failure depth" value={formatWithUnit(bearingCapacity.failureDepth, 2, 'm')} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <Box>
              <DetailRow label="Applied surcharge q" value={formatWithUnit(bearingCapacity.surcharge, 2, 'kPa')} />
              <DetailRow label="Footing width b" value={formatWithUnit(bearingCapacity.footingWidth, 3, 'm')} />
              <DetailRow label="Footing length l" value={formatWithUnit(bearingCapacity.footingLength, 3, 'm')} />
            </Box>
            <Box>
              <DetailRow label="Embedment depth" value={formatWithUnit(bearingCapacity.embedmentDepth, 3, 'm')} />
              <DetailRow label="Embedment unit weight" value={formatWithUnit(bearingCapacity.embedmentUnitWeight, 2, 'kN/m³')} />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary">
            {bearingCapacity.note}
          </Typography>

          {canUserSeeCalculationSteps && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Layer checks for the governing bearing-capacity estimate</Typography>
              <BearingLayerTable layers={bearingCapacity.layers} />
            </Stack>
          )}
        </StageSection>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Design Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            This wraps up the governing outputs after following the Priebe stages above.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <ValueCard label="Footing case" value={footingTypeLabel} />
            <ValueCard label="Area ratio A/Ac" value={formatNumber(geometry.areaRatio, 3)} />
            <ValueCard label="Basic improvement n0" value={formatNumber(factors.n0, 3)} />
            <ValueCard label="Representative n1" value={formatNumber(topTreatedLayer?.n1, 3)} />
            <ValueCard label="Representative n2" value={formatNumber(topTreatedLayer?.n2Avg, 3)} />
            <ValueCard
              label={footingType === 'unlimitedArea' ? 'Final settlement' : 'Footing settlement'}
              value={formatWithUnit(settlements.totalFootingSettlementCm, 2, 'cm')}
            />
            <ValueCard label="Infinite-area settlement" value={formatWithUnit(settlements.totalInfiniteAreaSettlementCm, 2, 'cm')} />
            <ValueCard
              label="Settlement ratio s/s∞"
              value={footingType === 'unlimitedArea' ? '1.00' : formatNumber(settlements.footingToInfiniteRatio, 3)}
            />
            <ValueCard
              label="Bearing safety factor eta"
              value={bearingCapacity ? formatNumber(bearingCapacity.safetyFactor, 3) : 'Not used'}
            />
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default PriebeResult;
